import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import { api } from '@/utils/api'
import { useUser } from '@/hooks/useUser'

import type { GoogleAccount, OrganizationMember, User } from '@/types'
import type { User as ContextUser } from '@/context/UserContext'

type AccountExtended = GoogleAccount & {
  user: User
}

type Me = {
  accounts: AccountExtended[]
  members: OrganizationMember[]
}

const fetchMe = async (setUser: (args: ContextUser) => void) => {
  try {
    const resp = await api.get('/users/me')

    if (resp.data === null) {
      if (window.location.pathname !== '/login') {
        await api.post('/auth/logout')
      }
      return null
    }

    const { accounts, members } = resp.data.data
    const _hasOrgLinksAccess = accounts.some((a: AccountExtended) => a.hd)
    const _orgId = members?.[0]?.organizationId
    const _hasPersonalLinksAccess = accounts.some(
      (a: AccountExtended) => a.hd === null
    )

    if (resp.data.data) {
      const primaryUser =
        accounts.length === 1
          ? accounts[0].user
          : accounts.find(
              (a: AccountExtended) => a.user.id === a.user.primaryUserId
            ).user

      setUser({
        name: primaryUser.name,
        email: primaryUser.email,
        image: primaryUser.image,
        userIds: accounts.map((a: AccountExtended) => a.userId),
        organizationId: _hasOrgLinksAccess ? _orgId : undefined,
        hasOrgAccess: _hasOrgLinksAccess,
        hasPersonalLinksAccess: _hasPersonalLinksAccess,
      })
    }

    return resp.data.data
  } catch (error) {
    return error
  }
}

export const useMe = (enabled = true) => {
  const { setUser } = useUser()

  return useQuery<Me | AxiosError>({
    enabled: enabled,
    queryKey: ['me'],
    queryFn: () => fetchMe(setUser),
  })
}
